import { useState, useEffect, useMemo } from 'react';
import { Loader2, Send, Copy, Globe, Rss } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

interface Submission {
  id: string;
  url: string;
  service: string | null;
  status_code: number | null;
  response: string | null;
  submitted_at: string;
}

const SERVICE_LABEL: Record<string, string> = {
  indexnow: 'IndexNow',
  bing: 'Bing API',
  'sitemap-ping': 'Sitemap ping',
};

const AdminIndexNow = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState<Submission[]>([]);
  const [key, setKey] = useState('');
  const [domain, setDomain] = useState('');
  const [sitemap, setSitemap] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    const [s, h] = await Promise.all([
      supabase.from('blog_settings').select('indexnow_key, domain_name, sitemap_url').eq('id', 1).maybeSingle(),
      supabase.from('indexnow_submissions').select('*').order('submitted_at', { ascending: false }).limit(200),
    ]);
    setKey(s.data?.indexnow_key || '');
    setDomain(s.data?.domain_name || '');
    setSitemap(s.data?.sitemap_url || '');
    setRows((h.data as Submission[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter(r => (r.service || 'indexnow') === filter)),
    [rows, filter],
  );

  const run = async (fn: 'indexnow-submit' | 'bing-submit', body: Record<string, unknown>, label: string) => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(fn, { body });
      if (error) throw error;
      if (data?.error) throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
      toast({
        title: label,
        description: data.submitted != null
          ? `${data.submitted} URL(s), status ${data.status ?? 'n/a'}`
          : `Status ${data.status ?? 'n/a'}`,
      });
      load();
    } catch (e) {
      toast({ title: 'Submission failed', description: (e as Error).message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Search Engine Submission</h1>
          <p className="text-muted-foreground mt-1">
            Push new and updated URLs to Bing, Yandex and partners via IndexNow and the Bing Webmaster API
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>IndexNow</CardTitle>
            <CardDescription>Verification key must be reachable at https://{domain}/{key}.txt</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-muted px-3 py-2 text-sm break-all">{key}</code>
              <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(key); toast({ title: 'Key copied' }); }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => run('indexnow-submit', { scope: 'blog' }, 'Submitted to IndexNow')} disabled={busy}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Submit all blog posts
              </Button>
              <Button variant="outline" onClick={() => run('indexnow-submit', { scope: 'site' }, 'Submitted to IndexNow')} disabled={busy}>
                <Globe className="mr-2 h-4 w-4" />Submit whole site
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bing Webmaster API</CardTitle>
            <CardDescription>
              Direct URL submission to Bing. Subject to your daily Bing quota — quota errors appear in the history below.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button onClick={() => run('bing-submit', { scope: 'blog' }, 'Submitted to Bing')} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Submit all blog posts
            </Button>
            <Button variant="outline" onClick={() => run('bing-submit', { scope: 'site' }, 'Submitted to Bing')} disabled={busy}>
              <Globe className="mr-2 h-4 w-4" />Submit whole site
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sitemap</CardTitle>
            <CardDescription className="break-all">{sitemap || 'No sitemap URL configured'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" onClick={() => run('bing-submit', { action: 'ping' }, 'Sitemap pinged')} disabled={busy}>
              <Rss className="mr-2 h-4 w-4" />Announce sitemap via IndexNow
            </Button>
            <p className="text-sm text-muted-foreground">
              Google (2023) and Bing both retired their sitemap ping endpoints, so the sitemap URL is announced
              through IndexNow instead. Google picks the sitemap up from the <code>Sitemap:</code> line in robots.txt —
              submit it once in Search Console for the fastest pickup.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
            <CardTitle className="text-lg">Submission history</CardTitle>
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="indexnow">IndexNow</TabsTrigger>
                <TabsTrigger value="bing">Bing</TabsTrigger>
                <TabsTrigger value="sitemap-ping">Sitemap</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>URL</TableHead><TableHead className="w-28">Engine</TableHead><TableHead className="w-24">Status</TableHead><TableHead className="w-44">Submitted</TableHead><TableHead className="w-24" /></TableRow></TableHeader>
                <TableBody>
                  {visible.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No submissions yet</TableCell></TableRow>}
                  {visible.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="text-sm break-all">{r.url}</TableCell>
                      <TableCell><Badge variant="secondary">{SERVICE_LABEL[r.service || 'indexnow'] ?? r.service}</Badge></TableCell>
                      <TableCell><Badge variant={r.status_code && r.status_code < 300 ? 'default' : 'destructive'}>{r.status_code ?? 'error'}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(r.submitted_at).toLocaleString('en-AU')}</TableCell>
                      <TableCell className="text-right">
                        {(r.service || 'indexnow') !== 'sitemap-ping' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => run(
                              (r.service || 'indexnow') === 'bing' ? 'bing-submit' : 'indexnow-submit',
                              { urls: [r.url] },
                              'Resubmitted',
                            )}
                          >
                            Resubmit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminIndexNow;
